import { useMemo } from 'react';
import { oneDark } from '@codemirror/theme-one-dark';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import { Extension } from '@codemirror/state';
import { EditorView, highlightActiveLine, keymap, lineNumbers, drawSelection, rectangularSelection, crosshairCursor } from '@codemirror/view';
import { foldGutter, foldKeymap } from '@codemirror/language';
import { showMinimap } from '@replit/codemirror-minimap';
import { vim } from '@replit/codemirror-vim';
import { useUI, useSettings, useEditorState } from '../contexts';
import { useMultiCursor } from './useMultiCursor';
import { useExpandSelection } from './useExpandSelection';
import { useGitBlame } from './useGitBlame';
import { useSnippets } from './useSnippets';
import { useFoldByIndent } from './useFoldByIndent';
import { useHoverDocs } from './useHoverDocs';
import { useOrganizeImports } from './useOrganizeImports';
import { useSignatureHelp } from './useSignatureHelp';
import { useQuickFix } from './useQuickFix';
import { useExtractRefactor } from './useExtractRefactor';
import { GitBlameLine } from './useGit';

type FileItem = { name: string; path: string; type: 'file' | 'folder' };

type UseEditorConfigParams = {
  languageExtensions: Extension[];
  lintExtension: Extension;
  handleSave: () => Promise<boolean>;
  handleFormat: () => boolean;
  additionalExtensions?: Extension[];
  blameData?: GitBlameLine[];
  fileList?: FileItem[];
  activePath?: string | null;
};

export function useEditorConfig({
  languageExtensions,
  lintExtension,
  handleSave,
  handleFormat,
  additionalExtensions = [],
  blameData = [],
  fileList = [],
  activePath = null,
}: UseEditorConfigParams) {
  // Get theme from settings context
  const { theme, minimapEnabled, vimEnabled, blameEnabled, fontSize, increaseFontSize, decreaseFontSize, resetFontSize } = useSettings();

  // Get code from editor state context
  const { code } = useEditorState();

  // Get UI state from context
  const {
    setCommandPaletteOpen,
    setCommandQuery,
    setCommandHighlight,
    setGoToFileOpen,
  } = useUI();

  // Multi-cursor support
  const { multiCursorExtension } = useMultiCursor();

  // Expand/Shrink selection support
  const { expandSelectionExtension } = useExpandSelection();

  const editorThemeExtension = useMemo(
    () =>
      EditorView.theme(
        {
          '&': {
            backgroundColor: 'transparent',
            color: 'var(--text)',
            fontFamily: "'JetBrains Mono', 'SFMono-Regular', Consolas, monospace",
            fontSize: `${fontSize}px`,
          },
          '.cm-content': {
            fontSize: `${fontSize}px`,
            lineHeight: '1.5',
          },
          '.cm-gutters': {
            backgroundColor: 'transparent',
            color: 'var(--muted)',
            borderRight: '1px solid var(--border)',
            fontSize: `${fontSize}px`,
          },
          '.cm-activeLineGutter': {
            color: 'var(--text)',
            backgroundColor: 'var(--panel-subtle)',
          },
          '.cm-activeLine': {
            backgroundColor: 'var(--panel-subtle)',
          },
          '.cm-selectionBackground, .cm-content ::selection': {
            backgroundColor: 'rgba(34, 211, 238, 0.18)',
          },
          '.cm-lineNumbers .cm-gutterElement': {
            padding: '0 8px',
          },
        },
        { dark: theme === 'dark' }
      ),
    [theme, fontSize]
  );

  // Minimap extension configuration
  const minimapExtension = useMemo(
    () =>
      minimapEnabled
        ? showMinimap.compute(['doc'], () => ({
            create: () => ({ dom: document.createElement('div') }),
            displayText: 'blocks',
            showOverlay: 'always',
          }))
        : [],
    [minimapEnabled]
  );

  // Vim mode extension configuration
  const vimExtension = useMemo(
    () => (vimEnabled ? vim() : []),
    [vimEnabled]
  );

  // Git blame extension configuration
  const { blameExtension } = useGitBlame({
    enabled: blameEnabled,
    blameData,
  });

  // Snippet completions with path autocomplete
  const { snippetExtension } = useSnippets({ fileList, activePath });

  // Fold by indent level
  const { foldByIndentExtension } = useFoldByIndent();

  // Hover documentation
  const { hoverDocsExtension } = useHoverDocs({ enabled: true, code });

  // Organize imports
  const { organizeImportsExtension } = useOrganizeImports();

  // Signature help (parameter hints)
  const { signatureHelpExtension } = useSignatureHelp();

  // Quick fix menu (Cmd+.)
  const { quickFixExtension } = useQuickFix();

  // Extract refactoring (Cmd+Shift+M for function, Alt+Shift+V for variable)
  const { extractRefactorExtension } = useExtractRefactor();

  const editorExtensions = useMemo<Extension[]>(
    () => [
      lineNumbers(),
      highlightActiveLine(),
      drawSelection(), // Multi-cursor selection drawing
      rectangularSelection(), // Alt+Shift+Drag for box/column selection
      crosshairCursor(), // Show crosshair cursor during rectangular selection
      history(),
      keymap.of([
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        indentWithTab,
        {
          key: 'Mod-s',
          preventDefault: true,
          run: () => {
            void handleSave();
            return true;
          },
        },
        {
          key: 'Mod-Shift-f',
          run: () => handleFormat(),
        },
        {
          key: 'Mod-Shift-p',
          preventDefault: true,
          run: () => {
            setCommandPaletteOpen(true);
            setCommandQuery('');
            setCommandHighlight(0);
            setGoToFileOpen(false);
            return true;
          },
        },
        {
          key: 'Mod-=',
          preventDefault: true,
          run: () => {
            increaseFontSize();
            return true;
          },
        },
        {
          key: 'Mod--',
          preventDefault: true,
          run: () => {
            decreaseFontSize();
            return true;
          },
        },
        {
          key: 'Mod-0',
          preventDefault: true,
          run: () => {
            resetFontSize();
            return true;
          },
        },
      ]),
      multiCursorExtension, // Cmd+D, Cmd+Shift+L, etc.
      expandSelectionExtension, // Shift+Alt+Right/Left for expand/shrink selection
      foldGutter(),
      EditorView.lineWrapping,
      editorThemeExtension,
      ...(theme === 'dark' ? [oneDark] : []),
      ...languageExtensions,
      lintExtension,
      minimapExtension,
      vimExtension,
      blameExtension,
      snippetExtension,
      foldByIndentExtension,
      hoverDocsExtension,
      organizeImportsExtension,
      signatureHelpExtension,
      quickFixExtension,
      extractRefactorExtension,
      ...additionalExtensions,
    ],
    [editorThemeExtension, handleFormat, handleSave, languageExtensions, lintExtension, theme, setCommandPaletteOpen, setCommandQuery, setCommandHighlight, setGoToFileOpen, multiCursorExtension, expandSelectionExtension, minimapExtension, vimExtension, blameExtension, snippetExtension, foldByIndentExtension, hoverDocsExtension, organizeImportsExtension, signatureHelpExtension, quickFixExtension, extractRefactorExtension, additionalExtensions, increaseFontSize, decreaseFontSize, resetFontSize]
  );

  return { editorThemeExtension, editorExtensions };
}
